import React, { useState, useEffect, useCallback } from 'react';
import { getLeaveSummary } from '../api';
import { useAuth } from '../AuthContext';

const card = { background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 12, padding: 20, marginBottom: 16 };

// Collect the distinct leave-type names across all rows (for table columns).
function collectTypes(rows) {
  const seen = new Map();
  rows.forEach(r => r.balances.forEach(b => { if (!seen.has(b.name)) seen.set(b.name, b); }));
  return [...seen.values()];
}

export default function LeaveSummary() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await getLeaveSummary()); } catch { setRows([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const types = collectTypes(rows);
  const fmt = (b) => b.unlimited ? '∞' : String(b.remaining);
  const sub = (b) => b.unlimited ? b.category : `of ${b.allowed}${b.carried ? ` +${b.carried} carried` : ''} · ${b.used} used`;
  const cellColor = (b) => (!b.unlimited && b.remaining <= 0) ? '#dc2626' : (!b.unlimited && b.remaining <= 2 ? '#d97706' : 'var(--text)');

  return (
    <div style={{ padding: 24, maxWidth: 1050, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>Leave Summary</h1>
      <p style={{ color: 'var(--muted)', margin: '0 0 20px', fontSize: 14 }}>
        Remaining / allowed days per leave type{isAdmin ? ' for your team' : ''}. Cells show <strong>remaining/allowed</strong>.
      </p>

      {loading ? <div style={{ ...card, color: 'var(--muted)' }}>Loading…</div>
        : rows.length === 0 ? <div style={{ ...card, color: 'var(--muted)' }}>No leave data yet. Set up leave types in Settings → Leave Settings.</div>
        : (
          <div style={{ ...card, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr style={{ color: 'var(--muted)', textAlign: 'left' }}>
                  {isAdmin && <th style={{ padding: '9px 10px', fontWeight: 600, borderBottom: '1px solid var(--hairline)' }}>Employee</th>}
                  {types.map(t => <th key={t.name} style={{ padding: '9px 10px', fontWeight: 600, borderBottom: '1px solid var(--hairline)' }}>{t.name}</th>)}
                  {types.length === 0 && <th style={{ padding: '9px 10px', color: 'var(--muted)' }}>No leave types assigned</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.employee_id} style={{ borderBottom: '1px solid var(--hairline)' }}>
                    {isAdmin && <td style={{ padding: '10px', fontWeight: 600 }}>{r.employee_name}<div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>{r.department || ''}</div></td>}
                    {types.map(t => {
                      const b = r.balances.find(x => x.name === t.name);
                      return (
                        <td key={t.name} style={{ padding: '10px' }}>
                          {b ? (
                            <div>
                              <div style={{ fontWeight: 700, color: cellColor(b) }}>{fmt(b)} <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)' }}>left</span></div>
                              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{sub(b)}</div>
                            </div>
                          ) : <span style={{ color: 'var(--muted)' }}>—</span>}
                        </td>
                      );
                    })}
                    {types.length === 0 && <td style={{ padding: '10px', color: 'var(--muted)' }}>—</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
